/**
 * A web component that displays a 3D coordinate system visualization.
 * This component renders X, Y, and Z axes with colored endpoints and labels,
 * updating in real-time based on camera position.
 * 
 * @component
 * 
 * @example
 * ```html
 * <!-- Using attributes -->
 * <as-axis-display width="200px" height="150px" top="10px" right="10px" offset-x="5px" offset-y="-5px"></as-axis-display>
 * 
 * <!-- Using CSS variables -->
 * <style>
 *   as-axis-display {
 *     --offset-x: 5px;
 *     --offset-y: -5px;
 *   }
 * </style>
 * <as-axis-display width="200px" height="150px" top="10px" right="10px"></as-axis-display>
 * 
 * <!-- Attributes take precedence over CSS variables -->
 * <style>
 *   as-axis-display {
 *     --offset-x: 5px;
 *     --offset-y: -5px;
 *   }
 * </style>
 * <as-axis-display offset-x="10px" offset-y="-10px"></as-axis-display>
 * <!-- Will use 10px and -10px, ignoring CSS variables -->
 * ```
 * 
 * @attributes
 * - width: Sets the width of the canvas (supports px, %, rem)
 * - height: Sets the height of the canvas (supports px, %, rem)
 * - top: Sets the top position (supports px, %, rem)
 * - right: Sets the right position (supports px, %, rem)
 * - bottom: Sets the bottom position (supports px, %, rem)
 * - left: Sets the left position (supports px, %, rem)
 * - offset-x: Fine-tunes horizontal position (supports px, rem)
 * - offset-y: Fine-tunes vertical position (supports px, rem)
 * 
 * @css
 * - canvas: The main canvas element
 *   - width: 100% (default, can be overridden by width attribute)
 *   - height: 100% (default, can be overridden by height attribute)
 *   - position: absolute (for positioning)
 * - --offset-x: CSS variable for horizontal offset (default: 0)
 * - --offset-y: CSS variable for vertical offset (default: 0)
 * 
 * @precedence
 * - Attributes take precedence over CSS variables
 * - CSS variables provide default values when attributes are not set
 * - Both support px and rem units
 * 
 * @events
 * - camera-update: Dispatched when camera parameters change
 *   - detail: { cam: { right: number[], top: number[], view: number[] } }
 * 
 * @internal
 * - Uses HTML5 Canvas for rendering
 * - Automatically resizes with parent container
 * - Maintains aspect ratio of coordinate system
 * - Renders axes in order of depth (z-index)
 */

import { stylesheetFromString } from "../../logic/utils.ts";

interface Point3D {
  point: DOMPoint;
  color: string;
  label: string;
  labelCol: string;
}

interface Camera {
  right: number[];
  top: number[];
  view: number[];
}

/**
 * Updates and renders the coordinate system display
 */
function renderAxes(camera: Camera, canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): void {
  // Create camera transformation matrix
  const cameraMatrix = new DOMMatrix([
    -camera.right[0], -camera.right[1], -camera.right[2], 0,
    camera.top[0], camera.top[1], camera.top[2], 0,
    camera.view[0], camera.view[1], camera.view[2], 0,
    0, 0, 0, 1
  ]);

  // Rotate 90 degrees around X-axis
  const xAxisRotation = new DOMMatrix([
    1, 0, 0, 0,
    0, Math.cos(-Math.PI / 2), -Math.sin(-Math.PI / 2), 0,
    0, Math.sin(-Math.PI / 2), Math.cos(-Math.PI / 2), 0,
    0, 0, 0, 1
  ]);

  // Apply transformations
  cameraMatrix.preMultiplySelf(xAxisRotation);
  cameraMatrix.invertSelf();

  // Setup drawing parameters
  const size = canvas.clientWidth / 3;
  const dotSize = size / 6;
  const centerX = canvas.clientWidth / 2;
  const centerY = canvas.clientHeight / 2;

  // Define axis points with colors and labels
  const axisPoints: Point3D[] = [
    { point: new DOMPoint(-size, 0, 0, 1).matrixTransform(cameraMatrix), color: 'red', label: 'X', labelCol: '#ffaea8' },
    { point: new DOMPoint(0, 0, -size, 1).matrixTransform(cameraMatrix), color: 'green', label: 'Y', labelCol: 'lightgreen' },
    { point: new DOMPoint(0, +size, 0, 1).matrixTransform(cameraMatrix), color: 'blue', label: 'Z', labelCol: 'skyblue' }
  ];

  // Sort points by z-index for proper rendering order
  axisPoints.sort((a, b) => a.point.z > b.point.z ? 1 : -1);

  // Clear canvas
  ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
  ctx.lineWidth = 2;

  // Render each axis
  axisPoints.forEach(({ point, color, label, labelCol }) => {
    // Draw line
    ctx.save();
    ctx.strokeStyle = color;
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(centerX + point.x, centerY + point.y);
    ctx.stroke();
    ctx.restore();

    // Draw endpoint dot
    ctx.save();
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(centerX + point.x, centerY + point.y, dotSize, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Draw label
    ctx.save();
    ctx.fillStyle = labelCol;
    ctx.font = 'bold 9px Arial';
    ctx.fillText(label, centerX + point.x - dotSize * 0.45, centerY + point.y + dotSize * 0.45);
    ctx.restore();
  });
}

const styleText = `
  :host {
    position: absolute;
    display: block;
    --offset-x: 0;
    --offset-y: 0;
  }
  canvas {
    width: 100%;
    height: 100%;
  }
`;

export class AsAxisDisplay extends HTMLElement {
  private canvas: HTMLCanvasElement | undefined;
  private ctx: CanvasRenderingContext2D | undefined;
  private resizeObserver: ResizeObserver | undefined;

  static get observedAttributes(): string[] {
    return ['width', 'height', 'top', 'right', 'bottom', 'left', 'offset-x', 'offset-y'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.innerHTML = `
      <canvas class="axis-display"></canvas>
    `;
    this.shadowRoot!.adoptedStyleSheets = [stylesheetFromString(styleText)];
  }

  /**
   * Initialize the canvas and set up event listeners
   */
  connectedCallback() {
    this.canvas = this.shadowRoot!.querySelector('canvas');
    if (!this.canvas) return;
    
    this.ctx = this.canvas.getContext('2d');
    if (!this.ctx) return;

    this.updateCanvasSize();
    this.applySizeAttributes();
    this.applyPositionAttributes();
    
    this.resizeObserver = new ResizeObserver(
      () => {
        this.updateCanvasSize();
        if (this.canvas && this.ctx) {
          renderAxes({ right: [1, 0, 0], top: [0, 1, 0], view: [0, 0, 1] }, this.canvas, this.ctx);
        }
      }
    );
    this.resizeObserver.observe(this.canvas);
    
    window.addEventListener('as-viewport-camera-update', (e: CustomEvent<{cam: Camera}>) => {
      if (this.canvas && this.ctx) {
        renderAxes(e.detail.cam, this.canvas, this.ctx);
      }
    });
  }

  /**
   * Handle attribute changes
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;
    
    if (name === 'width' || name === 'height') {
      this.applySizeAttributes();
    } else if (['top', 'right', 'bottom', 'left', 'offset-x', 'offset-y'].includes(name)) {
      this.applyPositionAttributes();
    }
  }

  /**
   * Apply width and height attributes to canvas
   */
  private applySizeAttributes(): void {
    if (!this.canvas) return;

    const width = this.getAttribute('width');
    const height = this.getAttribute('height');

    if (width) {
      this.canvas.style.width = width;
    }
    if (height) {
      this.canvas.style.height = height;
    }
  }

  /**
   * Apply positioning attributes to host element
   * Attributes take precedence over CSS variables
   */
  private applyPositionAttributes(): void {
    const positions = ['top', 'right', 'bottom', 'left'];
    positions.forEach(pos => {
      const value = this.getAttribute(pos);
      if (value) {
        this.style[pos] = value;
      }
    });

    // Apply offset attributes or CSS variables
    // Attributes take precedence over CSS variables
    const offsetX = this.getAttribute('offset-x') ?? getComputedStyle(this).getPropertyValue('--offset-x').trim();
    const offsetY = this.getAttribute('offset-y') ?? getComputedStyle(this).getPropertyValue('--offset-y').trim();
    
    if (offsetX) {
      this.style.transform = `translateX(${offsetX})`;
    }
    if (offsetY) {
      this.style.transform = `translateY(${offsetY})`;
    }
    if (offsetX && offsetY) {
      this.style.transform = `translate(${offsetX}, ${offsetY})`;
    }
  }

  /**
   * Clean up event listeners and observers
   */
  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  /**
   * Update canvas size based on client rect
   */
  private updateCanvasSize(): void {
    if (!this.canvas) return;
    const rect = this.canvas.getBoundingClientRect();
    this.canvas.width = rect.width;
    this.canvas.height = rect.height;
  }
}

// Check if the element is already defined before registering it
if (!customElements.get('as-axis-display')) {
  customElements.define('as-axis-display', AsAxisDisplay);
} else {
  console.warn("as-axis-display already defined");
}
